import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Input from '../../atoms/Input';
import DropUpArrow from '../../atoms/Icons/DropUpArrow';
import Information from '../../atoms/Icons/Information';
import CopyrightIcon from '../../atoms/Icons/CopyrightIcon';
import SubmitIcon from '../../atoms/Icons/SubmitIcon';
import subscribeService from '../../../services/subscribe';
import validateEmail from '../../../helpers/utils/validateEmail';
import footerMenuList from '../../../helpers/utils/footerMenuList';
import getCurrentYear from '../../../helpers/utils/getCurrentYear';
import eventSocialMedia from 'ui/lib/gtm/eventSocialMedia';
import eventSubsEmail from 'ui/lib/gtm/eventSubsEmail';
import eventDownloadAPP from 'ui/lib/gtm/eventDownloadAPP';
import eventItemFooter from 'ui/lib/gtm/eventItemFooter';
import BodyLarge from '../../atoms/Text/BodyLarge';
import { IRootState } from '../../../store';
import FooterDesktopSVG from '../../../assets/images/footer-desktop.svg';
import FooterMobileSVG from '../../../assets/images/footer-mobile.svg';
import useImpressionHomepage from '../../../hooks/emarsys/homepage/useImpressionHomepage';
import router from 'next/router';

interface FooterProps {
  enableSharing?: boolean;
  screenSizeData?: {
    screenSize: {
      height: number;
      mobile: boolean;
      width: number;
    };
  };
  footerRef?: React.RefObject<HTMLDivElement>;
  auto2000FooterLogoMobileClassName?: string;
  impressionData?: {
    title: string;
    section: string;
  };
}

const Footer = ({
  enableSharing = false,
  screenSizeData = { screenSize: { height: 673, mobile: false, width: 1920 } },
  footerRef = null,
  auto2000FooterLogoMobileClassName,
  impressionData,
}: FooterProps) => {
  const { screenSize } = enableSharing
    ? screenSizeData
    : useSelector((state: IRootState) => state.page);
  const [email, setEmail] = useState('');
  const [hasSubscribedEmail, setHasSubscribedEmail] = useState<string | boolean>('');
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [isSubscribeFieldFocus, setIsSubscribeFieldFocus] = useState(false);
  const currentYear = getCurrentYear();

  const PUBLIC_URL = process.env.NEXT_PUBLIC_URL;

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setHasSubscribedEmail('');
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (email.trim() === '') {
      alert('Email tidak boleh kosong');
      return;
    }
    if (!validateEmail(email)) {
      return setIsEmailValid(false);
    } else {
      eventSubsEmail(email);
      setIsEmailValid(true);
    }

    const response = await subscribeService.getSubscribeData(email);
    setHasSubscribedEmail(response.data.emailSaved);
    setEmail(email);
  };

  const getMessage = () => {
    switch (hasSubscribedEmail) {
      case true:
        return (
          <div className="rounded-[8px] p-[16px] flex flex-col gap-[16px] mt-[10px] bg-[#FFFEFE] w-[327px]">
            <p className="text-reliableBlack font-bold">Terima Kasih</p>
            <div>
              <p className="text-[12px] leading-[15px] text-reliableBlack70 font-[500]">
                Anda sudah berhasil Subscribe.
              </p>
              <a href={`${PUBLIC_URL}/c/mobil-baru-toyota`}>
                <div className="flex justify-between items-center">
                  <p className="text-[12px] leading-[15px] text-[#CE181E] font-bold">
                    Silakan telusuri Auto2000 Digiroom
                  </p>
                  <div className="rotate-90 rounded-full border-[2px] border-supportiveRed p-[10px] flex items-center justify-center">
                    <DropUpArrow fill="#CE181E" />
                  </div>
                </div>
              </a>
            </div>
          </div>
        );
      case false:
        return (
          <div className="flex items-center gap-[8px] mt-[14px]">
            <Information />
            <BodyLarge className={'text-[#FFE293]'}>Anda sudah berlangganan</BodyLarge>
          </div>
        );
      default:
        return '';
    }
  };
  const setSessionForBookingService = (targetLink: string) => {
    if (
      targetLink.includes('layanan-bengkel') ||
      targetLink.includes('body-paint-service') ||
      targetLink.includes('ths-home-service')
    ) {
      sessionStorage.setItem('OPEN_DECISION_MODAL', 'true');
    }
  };

  //emarsys impression
  const impressionRef = useRef(null);
  if (impressionData) {
    useImpressionHomepage(impressionRef, impressionData.title, impressionData.section);
  }

  const removeDsrValue = () => {
    if (router.asPath !== '/hubungi-kami' && sessionStorage.getItem('dsrValue')) {
      sessionStorage?.removeItem('dsrValue');
    }
  };

  return (
    <>
      <footer
        ref={impressionData ? impressionRef : footerRef}
        className={`bg-[#2C2B2B] lg:bg-reliableBlack`}
      >
        <div className="container xl:px-0 pt-[22px] lg:pt-[24px] pb-2 lg:pb-1">
          <div className="grid grid-cols-1 lg:gap-x-10 lg:grid-cols-3 ">
            <div className="grid grid-cols-1 lg:col-span-2 pr-7">
              <div className="hidden lg:flex mx-4 md:mx-0 items-center gap-x-12 mb-8 relative -mt-1">
                {/* Astra & Toyota Icons */}
                <CopyrightIcon />
              </div>
              <div className="grid grid-cols-2 gap-x-6 md:gap-6 sm:grid-cols-2 lg:col-span-2 lg:grid-cols-4">
                {footerMenuList.menu(screenSize.mobile).map((item, index) => (
                  <div key={`footer-menu-group-${index}`}>
                    <ul
                      className={`${item.className} ${
                        index > 1 ? 'flex' : 'md:flex'
                      } flex-col gap-y-[14px] lg:gap-y-[12px] xl:w-[186px]`}
                    >
                      {item.item.map((item, itemIndex) =>
                        item.show ? (
                          <li key={`footer-menu-group-${index}-item-${itemIndex}`}>
                            <a
                              onClick={() => {
                                eventItemFooter(item);
                                setSessionForBookingService(item.path);
                                removeDsrValue();
                              }}
                              id={`footer-${item.id}-${itemIndex + 1}`}
                              href={enableSharing ? null : `${PUBLIC_URL}${item.path}`}
                              className={item.className}
                            >
                              {item.title}
                            </a>
                          </li>
                        ) : null
                      )}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col lg:items-center lg:h-[370px] pt-2 lg:pt-[11px] lg:pl-3">
              <div className="relative flex flex-col">
                {/* Subscribe */}
                <div className="flex w-full">
                  <form className="w-full" onSubmit={handleSubscribe}>
                    {hasSubscribedEmail ? (
                      <p className="text-[13px] font-normal text-reliableBlack3">Subscribe</p>
                    ) : (
                      <Input
                        id={'footer-subcribe-email'}
                        placeholder={'Email Anda'}
                        label={'Subscribe'}
                        labelClassName={'text-white font-normal text-xs lg:text-sm'}
                        icon={<SubmitIcon fill={isSubscribeFieldFocus ? '#fff' : '#393536'} />}
                        iconPosition={'right'}
                        type={'email'}
                        inputClassName={'py-4 !min-h-[52px] !border-[#5F5F5F]'}
                        iconClassName={`pl-[6px] pr-[12px] xl:pr-[19px] pt-[15px] xl:pt-[13px] pb-[11px] ${
                          isSubscribeFieldFocus ? '!border-[#CE181E]' : '!border-[#5F5F5F]'
                        }`}
                        containerClassName={'w-full'}
                        value={email}
                        onChange={handleEmailChange}
                        disabled={enableSharing}
                        handleOnClickIcon={handleSubscribe}
                        inputWrapperClassName="mt-2 lg:mt-[7px] bg-[#F8F8F8] focus-within:bg-[#CE181E] max-w-[320px] lg:w-full xl:w-[331px] h-[52px]"
                        onFocus={() => setIsSubscribeFieldFocus(true)}
                        onBlur={() => setIsSubscribeFieldFocus(false)}
                      />
                    )}
                    {!isEmailValid ? (
                      <div className="flex gap-[8px] mt-[14px] w-[296px]">
                        <div>
                          <Information fill="#FF797E" />
                        </div>
                        <BodyLarge className={'text-[#FF797E]'}>
                          Mohon masukkan email valid Contoh: peter@gmail.com
                        </BodyLarge>
                      </div>
                    ) : (
                      getMessage()
                    )}
                  </form>
                </div>

                {/* Social media icon */}
                <div className="flex flex-col mt-[30px] lg:mt-[25px] pl-1 lg:pl-0">
                  <span className="text-white text-xs md:text-sm font-bold md:uppercase">
                    IKUTI KAMI
                  </span>
                  <ul className="flex gap-x-[30px] -ml-1 md:ml-0 lg:gap-x-6 mt-[10px] xl:mt-2">
                    {footerMenuList.socialMedia(enableSharing).map((item, index) => (
                      <li key={`footer-social-item-${index}`}>
                        <a
                          onClick={() => eventSocialMedia(item.title)}
                          href={enableSharing ? null : item.link}
                          rel="noopener noreferrer"
                          target="_blank"
                          className={`text-white transition hover:opacity-75 ${(item.disabled as boolean) ? 'pointer-events-none cursor-default' : ''}`}
                        >
                          <span className="sr-only">{item.title}</span>
                          {item.icon}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Download on the Playstore */}
                <div className="flex flex-col pl-1 lg:pl-0">
                  <span className="text-white text-xs md:text-sm font-bold md:uppercase mb-[8.5px] xl:mb-[14px] lg:mb-3 mt-[21px] md:mt-10 xl:mt-[38px]">
                    GUNAKAN APLIKASI DIGIROOM
                  </span>
                  <div className="flex gap-x-[14px] lg:gap-x-[10px] xl:gap-x-[17px] lg:h-12">
                    {footerMenuList.downloadOn.map((item, index) => (
                      <a
                        onClick={() => eventDownloadAPP(item.id)}
                        key={`footer-download-${index}`}
                        id={`footer-${item.id}`}
                        href={enableSharing ? null : item.link}
                        rel="noopener noreferrer"
                        target="_blank"
                        className={
                          (enableSharing as boolean) ? 'pointer-events-none cursor-default' : ''
                        }
                      >
                        {item.icon}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Footer Image */}
        <div className="hidden mt-[11px] mb-[5px] lg:mt-0 lg:flex lg:justify-end">
          <img
            src={(FooterDesktopSVG as any).src}
            className="w-[555px]"
            alt="Footer"
            loading="lazy"
            fetchPriority="low"
            decoding="async"
          />
        </div>

        {/* Copyright Section */}
        <div className="flex flex-col bg-[#1F1F1F] lg:bg-reliableBlack pt-6 pb-[11px] md:pb-[11px] sm:pb-[11px] lg:pb-[17px] lg:pt-[22px] lg:flex-row lg:justify-center">
          <div className="container lg:hidden flex mx-auto md:mx-auto lg:mx-0 items-center">
            {/* Astra & Toyota Icons */}
            <CopyrightIcon />
          </div>
          <div className="flex flex-col mx-[15px] sm:container lg:mx-0 lg:mt-[19px] xl:ml-[26px] lg:flex-row lg:gap-x-[34px] lg:justify-center lg:items-end text-[#A3A3A3]">
            <a
              id="footer-roadside"
              href="tel:1500898"
              className="text-xs tracking-tight xl:tracking-normal mb-[18px] mt-[21px] md:my-[21px] sm:my-[21px] lg:my-0 text-[#A3A3A3]"
            >
              Emergency Road Assistance{' '}
              <span className="text-xs font-bold ml-[2px] md:ml-[2px] sm:ml-[2px] lg:ml-0">
                1500 898
              </span>
            </a>

            <span rel="noopener" className="text-xs uppercase xl:tracking-tight">
              &copy; {currentYear} AUTO2000 | Hak cipta dilindungi
            </span>
          </div>
        </div>

        <div
          className={`${auto2000FooterLogoMobileClassName ? auto2000FooterLogoMobileClassName : ''} pb-[75px] lg:hidden lg:pb-0 lg:mb-0 bg-[#1F1F1F]`}
        >
          <img
            src={(FooterMobileSVG as any).src}
            alt="Footer"
            loading="lazy"
            fetchPriority="low"
            decoding="async"
          />
        </div>
      </footer>
    </>
  );
};

export default Footer;
